// Can 68 step 3, the switch-over: prepare a change to what Neucleos charges (it changes nothing), read it, and apply it
// after the person approves its card. The same World tools Cleo uses (neucleos_*_commercial_change).
import { decodeCommercialChange as decode, type CommercialChange } from "@cleo/shared/commercialChangeCard";
import { decodeJsonObject, postLocalJson } from "./localJsonTransport";

// Can 47: the card's shape, rows and words are shared with Pocket (@cleo/shared/commercialChangeCard).
export {
  COMMERCIAL_CHANGE_CONSEQUENCE,
  COMMERCIAL_CHANGE_UNCHANGED_IF_MOVED,
  commercialChangeClosedText,
  commercialChangeConsequence,
  commercialChangeRows,
  priceOutcomeLines,
  type CommercialChange,
} from "@cleo/shared/commercialChangeCard";


export type CommercialChangeRead =
  | { readonly status: "reading" }
  | { readonly status: "loaded"; readonly change: CommercialChange }
  | { readonly status: "failed"; readonly reason: string };

async function call(
  worldKey: string,
  operation: "propose" | "read" | "apply",
  args: Record<string, unknown>,
  fetchImpl: typeof fetch,
) {
  const response = await postLocalJson(
    `/api/neucleos/commerce/change/${operation}`,
    { worldKey, ...args },
    fetchImpl,
    AbortSignal.timeout(30_000),
  );
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.ok !== true)
    throw new Error(
      typeof body?.reason === "string" ? body.reason : "The price change could not be confirmed.",
    );
  return body.value;
}

/** Any kind of change as a card (Can 68 step 4: a markup change's models, markup and start); nothing changes yet. */
export async function proposeCommercialChange(
  worldKey: string,
  kind: string,
  reason: string,
  input: Readonly<Record<string, unknown>>,
  fetchImpl: typeof fetch = fetch,
): Promise<CommercialChange> {
  return decode(await call(worldKey, "propose", { kind, reason, input }, fetchImpl));
}

/** The switch-over as a card: every new price, ready for the person to approve. Nothing changes yet. */
export async function proposeSwitchOver(
  worldKey: string,
  reason: string,
  fetchImpl: typeof fetch = fetch,
): Promise<CommercialChange> {
  return decode(await call(worldKey, "propose", { kind: "switch-over", reason }, fetchImpl));
}

export async function readCommercialChange(
  worldKey: string,
  changeKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<CommercialChangeRead> {
  try {
    const value = await call(worldKey, "read", { changeKey }, fetchImpl);
    if (value === null)
      return { status: "failed", reason: "Neucleos has no change with that key." };
    return { status: "loaded", change: decode(value) };
  } catch (error) {
    return {
      status: "failed",
      reason: error instanceof Error ? error.message : "Neucleos could not read this change.",
    };
  }
}

/** Apply exactly the change the card showed; the Desk records the person's approval of it first. */
export async function applyCommercialChange(
  worldKey: string,
  input: { readonly changeKey: string; readonly digest: string },
  fetchImpl: typeof fetch = fetch,
): Promise<CommercialChange> {
  const applied = decode(
    await call(worldKey, "apply", { changeKey: input.changeKey, digest: input.digest }, fetchImpl),
  );
  if (
    applied.changeKey !== input.changeKey ||
    applied.digest !== input.digest ||
    applied.status !== "applied"
  )
    throw new Error(
      "Neucleos did not confirm this exact change as applied. Read the prices before trying again.",
    );
  return applied;
}
